<template>
  <dialog-modal-fi
    ref="dialog"
    class="media-library-zoom-image-dialog-fi modal-fi--large"
    only-body-scrollable
    :no-max-height="isModalFullscreen"
    @after-open-transition="showImage"
    @close="isImageEnabled = false"
  >
    <template #header>
      <div class="media-library-zoom-image-dialog-fi__headline">
        {{ itemTitle }}
      </div>
    </template>
    <template #body>
      <div class="media-library-zoom-image-dialog-fi__media-container">
        <loading-indicator
          class="media-library-zoom-image-dialog-fi__loading-indicator"
          variant="spinnerLocal"
        />
        <cloud-image-fi
          v-if="isImageEnabled && mediaUrl"
          :key="mediaUrl"
          class="media-library-zoom-image-dialog-fi__media"
          :src="mediaUrl"
          :alt="itemTitle"
          :params="cloudImageParams"
          full-height
        />
      </div>
    </template>
    <template #footer>
      <div class="media-library-zoom-image-dialog-fi__details">
        <div class="media-library-zoom-image-dialog-fi__file-info">
          <div
            v-if="itemSource && mediaUrl"
            class="media-library-zoom-image-dialog-fi__file-source"
          >
            {{ $dict.get('media-Library.Items.Source-Label-Images') }}
            {{ itemSource }}
          </div>

          <div
            v-if="fileUrl"
            class="media-library-zoom-image-dialog-fi__file-size"
          >
            <span class="media-library-zoom-image-dialog-fi__file-size-value">
              {{ getFileSizeAndLabel(fileSizeInBytes ?? 0) }}</span
            >
            <span>{{ fileExtension }}</span>
          </div>
        </div>

        <a
          v-if="mediaUrl"
          :href="mediaUrl"
          class="button-fi__icon button-fi__primary"
          download
          @click="handleDownload"
        >
          <icon-fi icon="download" />
        </a>
      </div>
    </template>
  </dialog-modal-fi>
</template>

<script setup lang="ts">
import { CloudImageFi, DialogModalFi, IconFi, LoadingIndicator } from 'atoms';
import { computed, nextTick, ref, toRef, useTemplateRef, watch } from 'vue';

import { useBreakpoints } from '@/composables/breakpoint';
import { useEventHub } from '@/composables/eventHub';
import { useIsMounted } from '@/composables/isMounted';
import { useMediaLibrary } from '@/composables/mediaLibrary';

defineOptions({ name: 'MediaLibraryZoomImageDialogFi' });

interface Props {
  item?: Record<string, any>;
  isNewsroom?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  item: () => ({}),
  isNewsroom: false,
});

const { sendEvent } = useEventHub();
const {
  fileExtension,
  fileUrl,
  fileSizeInBytes,
  image,
  itemTitle,
  itemSource,
  getFileSizeAndLabel,
} = useMediaLibrary(toRef(props, 'item'));

const dialogRef = useTemplateRef('dialog');
const currentWindowWidth = ref(0);
const dprDesktop = 1.2;
const isImageEnabled = ref(false);
const isMounted = useIsMounted();

const { windowDimensions } = useBreakpoints();
const isModalFullscreen = computed(() =>
  isMounted.value ? currentWindowWidth.value < 600 : false,
);

const mediaUrl = computed(() => (props.isNewsroom ? image.value : fileUrl.value));

const dprParam = computed(() =>
  isMounted.value && window?.devicePixelRatio < dprDesktop ? `&dpr=${dprDesktop}` : '',
);
const cloudImageParams = computed(
  () =>
    `func=${props.isNewsroom ? 'fit' : 'boundmin'}&aspect_ratio=16:9&force_format=png${dprParam.value}`,
);

const updateWindowWidth = () => {
  if (IS_SSR) {
    return;
  }
  currentWindowWidth.value = window.innerWidth;
};

watch(
  () => windowDimensions.value,
  () => {
    updateWindowWidth();
  },
  { deep: true, immediate: true },
);

const show = () => {
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const showImage = async () => {
  isImageEnabled.value = true;

  if (IS_SSR) {
    return;
  }

  await nextTick();
  window.dispatchEvent(new Event('resize'));
};

const handleDownload = () => {
  sendEvent('custom', {}, 'download', {
    link: mediaUrl.value,
    name: itemTitle.value,
  });
};

defineExpose({ show, close });
</script>

<style src="./media-library-zoom-image-dialog-fi-unscoped" />
